
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import {
  Document,
} from "../../../../domain/document";
import { FormatReference } from "../../../../domain/format";
import { Patient } from "../../../../domain/patient";
import { EditingDocument } from "../../editor";
import { NewDocumentEditorBaseMixin } from ".";
import { DomainRegistry } from "../../../../domain";

@Component({
})
export default class NewQuotedDocumentEditorMixin extends Mixins(NewDocumentEditorBaseMixin) {
  @Prop()
  quotedDocumentId: number;

  @Prop({ default: false })
  shouldInheritFormat: boolean;

  @Prop()
  getDocumentForQuotation: (patientNo: string, documentId: number) => Promise<Document>;

  @Prop()
  deleteCreateToken: () => Promise<void>;

  @Watch("patient")
  onPatientUpdated(newValue: Patient, oldValue: Patient): void {
    this.loadNewDocument();
  }

  @Watch("format")
  onFormatUpdated(newValue: FormatReference, oldValue: FormatReference): void {
    this.loadNewDocument();
  }

  data() {
    return {
      isDocumentFormatloading: false,
    };
  }
  isDocumentFormatloading: boolean;

  created(): void {
    if (!this.quotedDocumentId) {
      this.$fatal(
        "引用文書ID未指定",
        "引用文書ID(quotedDocumentId)が指定されていません。"
      );
    }
    if (this.shouldInheritFormat) {
      this.$logger.info("指定された文書の" + this.$wording.format.name + "を自動的に取得します。");
      this.setQuotedDocumentFormat();
    }
  }

  private setQuotedDocumentFormat(): void {
    this.isDocumentFormatloading = true;
    this.getDocumentFormat(this.patientNo, this.quotedDocumentId)
    .then(f => {
      this.isDocumentFormatloading = false;
      if (!f) {
        this.$error(this.$wording.format.name + "取得失敗", "引用元文書の" + this.$wording.format.name + "の取得に失敗しました。");
      } else if (f.deleted) {
        this.$warn(this.$wording.format.name + "取得失敗", "引用元文書の" + this.$wording.format.name + "は削除されています。");
      } else if (f.expired) {
        this.$warn(this.$wording.format.name + "取得失敗", "引用元文書の" + this.$wording.format.name + "は使用期限が切れています。");
      } else {
        this.$logger.info("指定された文書の" + this.$wording.format.name + "を自動的に取得しました。(formatOriginalId:" + f.id + ")");
        this.format = f;
      }
    })
    .catch(e => {
      this.isDocumentFormatloading = false;
      this.$error(this.$wording.format.name + "取得失敗", "引用元文書の" + this.$wording.format.name + "の取得に失敗しました。", e);
    });
  }

  private async getDocumentFormat(patientNo: string, documentId: number): Promise<FormatReference> {
    if (!patientNo) { throw new Error("patientNo is empty!!!"); }
    if (!documentId) { throw new Error("documentId is empty!!!"); }
    const documentSummary = await DomainRegistry.document.getDocumentRepository().getDocumentSummary(this.patientNo, this.quotedDocumentId);
    if (!documentSummary) { throw new Error("引用元文書の情報取得に失敗しました。"); }
    if (documentSummary.patientNo !== patientNo) {
      // 現状では引用元文書の取得時に文書と患者番号の不一致でエラーになるので、このあとフォーマットを選択したとしても、結局文書を記載できない挙動になっている
      throw new Error("指定された患者IDと文書の患者IDが一致していません。");
    }
    return DomainRegistry.format.getFormatRepository().getFormatReference(documentSummary.formatOriginalId);
  }

  async initializeAndQuoteDocument(source: Document, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string): Promise<void> {
    const patientNo = this.patient
      ? this.patient.id
      : this.patientNo;
    try {
      const results = await Promise.all([
        this.initializeDocumentAsync(source, institutionNo, regionalInstitutionNo, hisInfoGetterParameter),
        this.getInitializedDocumentForQuotationAsync(patientNo, this.quotedDocumentId),
      ]);
      const documentForQuotation = results[1];
      if (documentForQuotation.unmanagedInformation && !documentForQuotation.unmanagedInformation.isQuotable) {
        if (this.deleteCreateToken) {
          await this.deleteCreateToken();
        }
        this.$suspend("引用転記不可", "引用転記できないDocuMaker文書でない文書です。");
      }
      const document = await this.mergeDocumentAndApplyPatientProfileAsync(source, documentForQuotation);
      if (document) {
        document.freezeItems();
      }
      this.editingSource = document;
    } catch (e) {
      this.$fatal(
        "引用・初期化失敗",
        "指定された文書(source)と施設番号(institutionNo, regionalInstitutionNo, hisInfoGetterParameter)の引用・初期化が失敗しました。",
        e
      );
    }
  }

  async initializeDocumentAsync(source: Document, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string): Promise<void> {
    if (this.initialize) {
      await this.initialize(source, institutionNo, regionalInstitutionNo, hisInfoGetterParameter);
    }
  }

  async getInitializedDocumentForQuotationAsync(patientId: string, quotedDocumentId: number): Promise<Document> {
    this.$logger.info("引用・転記元の文書を取得します。ID:" + quotedDocumentId);
    const document = await this.getDocumentForQuotation(patientId, quotedDocumentId);
    const editingDocument = new EditingDocument(document);
    // 値を取得する文書の計算式を走らせる必要あるのかどうか・・？
    // 不要な気がしないでもない
    try {
      await editingDocument.initialize(); // 計算式の更新等初期化する.
    } catch (e) {
      this.$error("エラー", e.message, e);
    }
    return document;
  }

  async validateDocumentForQuotation(): Promise<void> {
    const repository = DomainRegistry.document.getDocumentRepository();
    const unmanagedDocument = await repository.getUnmanagedDocument(this.quotedDocumentId);
    if (unmanagedDocument && !unmanagedDocument.isQuotable) {
      this.$suspend("引用転記不可", "引用転記できないDocuMaker文書でない文書です。");
    }
  }
}
